import testSettingsSubstrate from './test-substrate.json';
import testSettingsPotplant from './test-potplant.json';
import { callApi } from './general';
import { isset, formatObjectValues } from '../components/ModelView/utils';
import { modelVersions } from './constants';
import { analysisColumnKeys } from '../store/constants';

export const reuseTypes = {
    fixed: 'fixed',
    EC: 'EC',
};

// export const _ionParameters = [
//   { label: "EC", value: "EC", unit: "" },
//   { label: "pH", value: "pH", unit: "" },
//   { label: "NH4", value: "NH4", unit: "mmol/l" },
//   { label: "K", value: "K", unit: "mmol/l" },
//   { label: "Na", value: "Na", unit: "mmol/l" },
//   { label: "Ca", value: "Ca", unit: "mmol/l" },
//   { label: "Mg", value: "Mg", unit: "mmol/l" },
//   { label: "NO3", value: "NO3", unit: "mmol/l" },
//   { label: "Cl", value: "Cl", unit: "mmol/l" },
//   { label: "SO4", value: "SO4", unit: "mmol/l" },
//   { label: "H2PO4", value: "H2PO4", unit: "mmol/l" },
//   { label: "HCO3", value: "HCO3", unit: "mmol/l" },
//   { label: "Fe", value: "SO4", unit: "µmol/l" },
//   { label: "Mn", value: "Mn", unit: "µmol/l" },
//   { label: "Zn", value: "Zn", unit: "µmol/l" },
//   { label: "B", value: "B", unit: "µmol/l" },
//   { label: "Cu", value: "Cu", unit: "µmol/l" },
//   { label: "Mo", value: "Mo", unit: "µmol/l" },
// ];

/**
 * @typedef {('EC'|'pH'|'NH4'|'K'|'Na'|'Ca'|'Mg'|'NO3'|'Cl'|'SO4'|'H2PO4'|'HCO3'|'Fe'|'Mn'|'Zn'|'B'|'Cu'|'Mo')} TIonParameter
 */
export const ionParameters = [
    'EC',
    'pH',
    'NH4',
    'K',
    'Na',
    'Ca',
    'Mg',
    'NO3',
    'Cl',
    'SO4',
    'H2PO4',
    'HCO3',
    'Fe',
    'Mn',
    'Zn',
    'B',
    'Cu',
    'Mo',
];

export const units = {
    EC: '',
    pH: '',
    NH4: 'mmol/l',
    K: 'mmol/l',
    Na: 'mmol/l',
    Ca: 'mmol/l',
    Mg: 'mmol/l',
    NO3: 'mmol/l',
    Cl: 'mmol/l',
    SO4: 'mmol/l',
    H2PO4: 'mmol/l',
    HCO3: 'mmol/l',
    Fe: 'µmol/l',
    Mn: 'µmol/l',
    Zn: 'µmol/l',
    B: 'µmol/l',
    Cu: 'µmol/l',
    Mo: 'µmol/l',
};

/**
 * @typedef {Object} TIonValue
 * @property {string} feedWaterAnalysis - Number as string, empty string for no value
 * @property {string} oneToOneDotFive - Number as string, empty string for no value, for key use constants analysisColumnKeys.oneToOneDotFive
 * @property {string} drainAnalysis - Number as string, empty string for no value
 * @property {string} elecCorrectRecipe - Number as string, empty string for no value
 *
 * @typedef {Object.<string, TIonValue>} TIonValues
 *
 * @param {*} fillWithTestValues
 * @param {*} modelVersion
 * @returns {TIonValues} Ion values for input
 */
export const getIonValues = (fillWithTestValues, modelVersion) => {
    const testValues = ionTestValues(modelVersion);
    return ionParameters.reduce((prev, ion) => {
        if (fillWithTestValues) {
            return {
                ...prev,
                [ion]: formatObjectValues(testValues[ion]),
            };
        }

        // @todo filter out pH for feedwater ??
        // if (ion === "pH") {
        //   return {
        //     ...prev,
        //     [ion]: {
        //       "1_5_analysis": "",
        //       drainAnalysis: "",
        //     },
        //   };
        // }

        if (ion === 'pH' || ion === 'EC') {
            return {
                ...prev,
                [ion]: {
                    feedWaterAnalysis: '',
                    [analysisColumnKeys.oneToOneDotFive]: '',
                    drainAnalysis: '',
                },
            };
        }

        return {
            ...prev,
            [ion]: {
                feedWaterAnalysis: '',
                [analysisColumnKeys.oneToOneDotFive]: '',
                // matAnalysis: 0.0,
                drainAnalysis: '',
                elecCorrectRecipe: '',
            },
        };
    }, {});
};

/*
 * @property {Object} substraat
 * @property {boolean} substraat.steenwol
 * @property {boolean} substraat.urethane
 * @property {boolean} substraat.oasis_veen
 * @property {boolean} substraat.veen
 * @property {boolean} substraat.veen_meng
 * @property {boolean} substraat.kokos
 */
/**
 * @typedef {{
 *  'gebruikbaar': boolean,
 *  'drain_hergebruik': boolean,
 *  'chlorine': boolean,
 *  'phases vd': string[],
 *  'phases gt': string[],
 *  'streefwaarden vd': {[ionParameter: string]: number},
 *  'streefwaarden gt': {[ionParameter: string]: number},
 *  }} TSubstrateConfigRaw
 */
/**
 * @typedef {{[substrate: string]: TSubstrateConfigRaw}} TCropConfigRaw
 */
/**
 * @typedef {Object} TConfigModelSubstrateRaw
 * @property {Object} general
 * @property {{[crop: string]: TCropConfigRaw}} crop_options - Crop name (id) with it's settings
 * @property {string[]} ionParameters
 */
/**
 *
 * @returns {Promise<TConfigModelSubstrateRaw|Error>} A promise to the settings
 */
export const fetchSettingsSubstrate = (key) => {
    return callApi('get_settings_substrate', key).then((data) => {
        return { ...data.settings, ionParameters };
    });
};

export const fetchSettingsPotplants = (key) => {
    return callApi('get_settings_potplant', key).then((data) => {
        console.log('get_settings_potplant data:', data);
        return { ...data.settings, ionParameters };
    });
};

const ionTestValues = (modelVersion) =>
    ionParameters.reduce((prev, ion) => {
        const testSettings =
            modelVersion === modelVersions.substraat
                ? testSettingsSubstrate
                : testSettingsPotplant;

        let drainAnalysis;
        if (isset(testSettings['drain_meting'])) {
            drainAnalysis = isset(testSettings['drain_meting'][ion])
                ? testSettings['drain_meting'][ion]
                : null;
        } else if (
            isset(testSettings['tank_meting']) &&
            isset(testSettings['tank_meting'][ion])
        ) {
            drainAnalysis = testSettings['tank_meting'][ion];
        } else if (
            isset(testSettings['mat_meting']) &&
            isset(testSettings['mat_meting'][ion])
        ) {
            drainAnalysis = testSettings['mat_meting'][ion];
        } else {
            drainAnalysis = null;
        }

        return {
            ...prev,
            [ion]: {
                feedWaterAnalysis: isset(testSettings['feed_water'][ion])
                    ? testSettings['feed_water'][ion]
                    : null,
                [analysisColumnKeys.oneToOneDotFive]: isset(
                    testSettings['mat_meting'][ion]
                )
                    ? testSettings['mat_meting'][ion]
                    : null,
                // matAnalysis: 0.0,
                drainAnalysis,
            },
        };
    }, {});
